/**
 * Service for loading and accessing static lookup data
 * This data is exported from the database and stored as a consolidated JSON asset
 */
import { debugError, debugWarn } from '../utils/debug';

export interface LookupDataItem {
    id: number;
    name: string;
    code: string | null;
    parentLookupId: string | null;
    [key: string]: any;
}

export interface ConsolidatedLookupData {
    exportedAt: string;
    version: string;
    lookups: {
        [lookupTableName: string]: LookupDataItem[];
    };
}

export interface StateLookup extends LookupDataItem {
    id: number;
    name: string;
    abbreviation: string;
}

export interface CountyLookup extends LookupDataItem {
    id: number;
    name: string;
    code: string; // fips_code
    parentLookupId: string; // state_code
}

export interface UserRoleLookup extends LookupDataItem {
    id: number;
    name: string;
}

export interface UserStatusLookup extends LookupDataItem {
    id: number;
    name: string;
}

export interface UserTypeLookup extends LookupDataItem {
    id: number;
    name: string;
}

export interface FilingStatusLookup extends LookupDataItem {
    id: number;
    name: string;
}

export interface SubscriptionTypeLookup extends LookupDataItem {
    id: number;
    name: string;
}

export interface BillingTypeLookup extends LookupDataItem {
    id: number;
    name: string;
}

export interface AccentColorLookup extends LookupDataItem {
    id: number;
    name: string;
    hex_value: string;
}

export interface TeamTypeLookup extends LookupDataItem {
    id: number;
    name: string;
}

class LookupDataService {
    private consolidatedData: ConsolidatedLookupData | null = null;
    private loadingPromise: Promise<ConsolidatedLookupData> | null = null;

    /**
     * Load consolidated lookup data from gzipped JSON file
     */
    private async loadConsolidatedData(): Promise<ConsolidatedLookupData> {
        // Return cached data if available
        if (this.consolidatedData) {
            return this.consolidatedData;
        }

        // Return existing loading promise if already loading
        if (this.loadingPromise) {
            return this.loadingPromise;
        }

        // Start loading
        this.loadingPromise = this.fetchConsolidatedData();

        try {
            const data = await this.loadingPromise;
            this.consolidatedData = data;
            this.loadingPromise = null;
            return data;
        } catch (error) {
            this.loadingPromise = null;
            throw error;
        }
    }

    /**
     * Fetch consolidated lookup data from server (gzipped)
     * The browser will automatically decompress if the server sends Content-Encoding: gzip header
     */
    private async fetchConsolidatedData(): Promise<ConsolidatedLookupData> {
        try {
            const response = await fetch('/data/lookups.json.gz');

            if (!response.ok) {
                throw new Error(`Failed to load lookups.json.gz: ${response.status} ${response.statusText}`);
            }

            // Browser automatically decompresses when server sends Content-Encoding: gzip
            // Just parse as JSON - browser handles decompression transparently
            return await response.json() as ConsolidatedLookupData;
        } catch (error) {
            debugError('Error loading consolidated lookup data:', error);
            throw error;
        }
    }

    /**
     * Get lookup data by table name
     */
    private async getLookupData(tableName: string): Promise<LookupDataItem[]> {
        const data = await this.loadConsolidatedData();
        return data.lookups[tableName] || [];
    }

    /**
     * Convert snake_case to camelCase
     */
    private snakeToCamel(str: string): string {
        return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
    }

    /**
     * Get states data
     */
    async getStates(): Promise<StateLookup[]> {
        return await this.getLookupData('stateLookup') as StateLookup[];
    }

    /**
     * Get counties data
     */
    async getCounties(): Promise<CountyLookup[]> {
        return await this.getLookupData('countyLookup') as CountyLookup[];
    }

    /**
     * Get counties for a specific state
     */
    async getCountiesByState(stateCode: string): Promise<CountyLookup[]> {
        const counties = await this.getCounties();
        return counties.filter(county => county.parentLookupId === stateCode);
    }

    /**
     * Get user roles data (user_security_role_lookup)
     */
    async getUserRoles(): Promise<UserRoleLookup[]> {
        return await this.getLookupData('userSecurityRoleLookup') as UserRoleLookup[];
    }

    /**
     * Get user statuses data
     */
    async getUserStatuses(): Promise<UserStatusLookup[]> {
        return await this.getLookupData('userStatusLookup') as UserStatusLookup[];
    }

    /**
     * Get user types data (user_business_role_lookup)
     */
    async getUserTypes(): Promise<UserTypeLookup[]> {
        return await this.getLookupData('userBusinessRoleLookup') as UserTypeLookup[];
    }

    /**
     * Get team types data (team_type_lookup)
     */
    async getTeamTypes(): Promise<TeamTypeLookup[]> {
        return await this.getLookupData('teamTypeLookup') as TeamTypeLookup[];
    }

    /**
     * Get filing statuses data
     */
    async getFilingStatuses(): Promise<FilingStatusLookup[]> {
        return await this.getLookupData('filingStatusLookup') as FilingStatusLookup[];
    }

    /**
     * Get subscription types data
     */
    async getSubscriptionTypes(): Promise<SubscriptionTypeLookup[]> {
        return await this.getLookupData('subscriptionTypeLookup') as SubscriptionTypeLookup[];
    }

    /**
     * Get billing types data
     */
    async getBillingTypes(): Promise<BillingTypeLookup[]> {
        return await this.getLookupData('billingTypeLookup') as BillingTypeLookup[];
    }

    /**
     * Get accent colors data
     */
    async getAccentColors(): Promise<AccentColorLookup[]> {
        return await this.getLookupData('accentColorLookup') as AccentColorLookup[];
    }

    /**
     * Get any lookup data by table name (accepts either snake_case or camelCase)
     */
    async getLookupDataByTable(tableName: string): Promise<LookupDataItem[]> {
        // Convert to camelCase if needed
        const camelCaseName = tableName.includes('_') ? this.snakeToCamel(tableName) : tableName;
        return await this.getLookupData(camelCaseName);
    }

    /**
     * Get lookup data synchronously if already cached (for use outside React context)
     * Returns null if data is not yet loaded
     */
    getLookupDataByTableSync(tableName: string): LookupDataItem[] | null {
        if (!this.consolidatedData) {
            return null;
        }
        // Convert to camelCase if needed
        const camelCaseName = tableName.includes('_') ? this.snakeToCamel(tableName) : tableName;
        return this.consolidatedData.lookups[camelCaseName] || null;
    }

    /**
     * Clear cache (useful for development or when data is updated)
     */
    clearCache(): void {
        this.consolidatedData = null;
        this.loadingPromise = null;
    }

    /**
     * Preload consolidated lookup data
     */
    async preloadCommonData(): Promise<void> {
        try {
            await this.loadConsolidatedData();
        } catch (error) {
            debugWarn('Failed to preload lookup data:', error);
        }
    }
}

// Export singleton instance
export const lookupDataService = new LookupDataService();
